/**
 * AutoFirmaClient — thin wrapper around the Spanish Government's autoscript.js
 * client. The client is loaded lazily from /autofirma/autoscript.js when first
 * needed.
 *
 * Reference docs:
 *   https://administracionelectronica.gob.es/ctt/autofirma
 *   https://github.com/ctt-gob-es/clienteafirma
 */

let scriptPromise = null;

/**
 * Lazy-load autoscript.js exactly once.
 * Returns a promise that resolves when window.AutoScript is available.
 */
export function loadAutoScript() {
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        if (typeof window === "undefined") {
            reject(new Error("AutoScript: no window available"));
            return;
        }
        if (window.AutoScript) {
            resolve(window.AutoScript);
            return;
        }
        const script = document.createElement("script");
        script.src = "/autofirma/autoscript.js";
        script.async = true;
        script.onload = () => {
            if (window.AutoScript) {
                resolve(window.AutoScript);
            } else {
                reject(new Error("AutoScript no se cargó correctamente"));
            }
        };
        script.onerror = () =>
            reject(new Error("No se pudo descargar el cliente AutoFirma"));
        document.head.appendChild(script);
    });
    return scriptPromise;
}

/**
 * Sign a base64-encoded PDF using AutoFirma (PAdES format).
 *
 * @param {string} pdfBase64 - base64 of the unsigned PDF
 * @returns {Promise<{signedB64: string, certB64: string, dn: string}>}
 */
export async function signPdfWithAutoFirma(pdfBase64) {
    const AutoScript = await loadAutoScript();

    // Configure: store the certificate in default mode (Windows-MY / macOS keychain /
    // Mozilla NSS). PAdES signature format for PDFs.
    try {
        AutoScript.setStickySignatory(true);
    } catch (_e) {
        /* not all versions expose this */
    }
    try {
        AutoScript.setServlets(null, null);
    } catch (_e) {
        /* servlets only required for high-volume signing */
    }

    return new Promise((resolve, reject) => {
        AutoScript.sign(
            pdfBase64,
            "SHA512withRSA",
            "PAdES",
            "expPolicy=FirmaAGE", // standard policy for Spanish public administration
            (signatureB64, certificateB64) => {
                let dn = "";
                try {
                    // Best-effort: extract Subject DN from the cert if AutoScript provides it
                    if (
                        certificateB64 &&
                        typeof AutoScript.getCertificateSubject === "function"
                    ) {
                        dn = AutoScript.getCertificateSubject(certificateB64) || "";
                    }
                } catch (_e) {
                    /* ignore */
                }
                resolve({
                    signedB64: signatureB64,
                    certB64: certificateB64,
                    dn,
                });
            },
            (errorType, errorMessage) => {
                if (
                    /cancel/i.test(errorMessage || "") ||
                    /cancel/i.test(errorType || "")
                ) {
                    reject(new Error("Firma cancelada por el usuario"));
                } else if (/not found|no se ha encontrado/i.test(errorMessage || "")) {
                    reject(
                        new Error(
                            "AutoFirma no está instalado en este equipo. Instálalo en firmaelectronica.gob.es"
                        )
                    );
                } else {
                    reject(new Error(`${errorType}: ${errorMessage}`));
                }
            }
        );
    });
}
